import { useState, useEffect, useCallback, Fragment } from 'react';
import api from '../../hooks/useApi';
import SowSelect from '../shared/SowSelect';
import PoSelect from '../shared/PoSelect';
import Modal from '../shared/Modal';

const fmt = (n) => n != null ? '£' + Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const fmtFull = (n) => n != null ? '£' + Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

function trend(curr, prev) {
  if (!prev || prev === 0) return null;
  return ((curr - prev) / prev) * 100;
}

function TrendBadge({ pct }) {
  if (pct == null) return null;
  const up = pct > 0;
  return (
    <span className={`text-[8px] font-semibold ml-1 ${up ? 'text-red-500' : 'text-emerald-600'}`}>
      {up ? '▲' : '▼'}{Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function DomainBadge({ domain }) {
  if (domain === 'contractor') return <span className="text-[7px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-semibold">Labour</span>;
  if (domain === 'supplier')   return <span className="text-[7px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">Supplier</span>;
  if (domain === 'po')         return <span className="text-[7px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-semibold">PO</span>;
  return null;
}

const BLANK_SUPPLIER = {
  invoice_number: '', invoice_date: '', supplier_id: '', supplier_name: '',
  total_excl_vat: '', vat_amount: '', total_incl_vat: '', currency: 'GBP',
  sow_id: '', po_id: '', overhead_category_id: '', scheduled_payment_date: '', notes: '',
};

const BLANK_CONTRACTOR = {
  payee_name: '', invoice_number: '', invoice_date: '',
  period_from: '', period_to: '', total_amount: '',
  vat_amount: '', total_incl_vat: '', currency: 'GBP',
  sow_id: '', po_id: '', scheduled_payment_date: '', notes: '',
};

export default function OutgoingInvoicesPage() {
  const [granularity, setGranularity]           = useState('monthly');
  const [analytics, setAnalytics]               = useState(null);
  const [schedule, setSchedule]                 = useState([]);
  const [scheduleLoading, setScheduleLoading]   = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [horizonDays, setHorizonDays]           = useState('90');
  const [scheduleStatus, setScheduleStatus]     = useState('all');

  // Manual invoice modal
  const [showModal, setShowModal]       = useState(false);
  const [invoiceType, setInvoiceType]   = useState('supplier');
  const [supplierForm, setSupplierForm] = useState(BLANK_SUPPLIER);
  const [contractorForm, setContractorForm] = useState(BLANK_CONTRACTOR);
  const [sows, setSows]                 = useState([]);
  const [pos, setPos]                   = useState([]);
  const [suppliers, setSuppliers]       = useState([]);
  const [categories, setCategories]     = useState([]);
  const [saving, setSaving]             = useState(false);
  const [saveError, setSaveError]       = useState('');

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, domain, label, refreshPeriod? }
  const [deleting, setDeleting]         = useState(false);

  // Period drill-down
  const [expandedPeriod, setExpandedPeriod]           = useState(null);
  const [periodDetail, setPeriodDetail]               = useState({}); // { [period]: invoices[] }
  const [periodDetailLoading, setPeriodDetailLoading] = useState(new Set());

  const loadAnalytics = useCallback(() => {
    setAnalyticsLoading(true);
    api.get('outgoing-invoices/analytics', { granularity })
      .then(setAnalytics)
      .catch(() => setAnalytics(null))
      .finally(() => setAnalyticsLoading(false));
  }, [granularity]);

  const loadSchedule = useCallback(() => {
    setScheduleLoading(true);
    api.get('outgoing-invoices/payment-schedule', { days: horizonDays, status: scheduleStatus })
      .then(setSchedule)
      .catch(() => setSchedule([]))
      .finally(() => setScheduleLoading(false));
  }, [horizonDays, scheduleStatus]);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);
  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const openModal = () => {
    setShowModal(true);
    setSupplierForm(BLANK_SUPPLIER);
    setContractorForm(BLANK_CONTRACTOR);
    setSaveError('');
    Promise.all([
      sows.length    ? null : api.get('sows', { status: 'Active' }).then(setSows).catch(() => {}),
      pos.length     ? null : api.get('purchase-orders').then(setPos).catch(() => {}),
      suppliers.length ? null : api.get('suppliers').then(d => setSuppliers(d || [])).catch(() => {}),
      categories.length ? null : api.get('invoice-overhead-categories').then(setCategories).catch(() => {}),
    ]);
  };

  const saveManualInvoice = async () => {
    setSaving(true);
    setSaveError('');
    try {
      if (invoiceType === 'supplier') {
        const name = supplierForm.supplier_name ||
          (suppliers.find(s => s.id === supplierForm.supplier_id)?.name || '');
        if (!name && !supplierForm.supplier_id) {
          setSaveError('Select or enter a supplier name');
          setSaving(false);
          return;
        }
        await api.post('procurement/supplier-invoices', {
          ...supplierForm,
          supplier_name: name || supplierForm.supplier_name,
          source: 'manual',
          total_excl_vat:  supplierForm.total_excl_vat  ? parseFloat(supplierForm.total_excl_vat)  : null,
          vat_amount:      supplierForm.vat_amount       ? parseFloat(supplierForm.vat_amount)       : null,
          total_incl_vat:  supplierForm.total_incl_vat  ? parseFloat(supplierForm.total_incl_vat)  : null,
        });
      } else {
        if (!contractorForm.payee_name && !contractorForm.invoice_number) {
          setSaveError('Payee name or invoice number is required');
          setSaving(false);
          return;
        }
        await api.post('outgoing-invoices/manual-contractor', {
          ...contractorForm,
          total_amount:   contractorForm.total_amount    ? parseFloat(contractorForm.total_amount)   : null,
          vat_amount:     contractorForm.vat_amount      ? parseFloat(contractorForm.vat_amount)      : null,
          total_incl_vat: contractorForm.total_incl_vat ? parseFloat(contractorForm.total_incl_vat) : null,
        });
      }
      setShowModal(false);
      loadAnalytics();
      loadSchedule();
    } catch (e) {
      setSaveError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const togglePeriod = (period) => {
    if (expandedPeriod === period) { setExpandedPeriod(null); return; }
    setExpandedPeriod(period);
    if (periodDetail[period] !== undefined) return;
    setPeriodDetailLoading(s => new Set([...s, period]));
    api.get('outgoing-invoices/period-detail', { period })
      .then(data => setPeriodDetail(d => ({ ...d, [period]: data })))
      .catch(() => setPeriodDetail(d => ({ ...d, [period]: [] })))
      .finally(() => setPeriodDetailLoading(s => { const n = new Set(s); n.delete(period); return n; }));
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const path = deleteTarget.domain === 'supplier'
        ? `outgoing-invoices/supplier/${deleteTarget.id}`
        : `outgoing-invoices/contractor/${deleteTarget.id}`;
      await api.delete(path);
      const refreshPeriod = deleteTarget.refreshPeriod;
      setDeleteTarget(null);
      loadAnalytics();
      loadSchedule();
      if (refreshPeriod) {
        setPeriodDetail(d => { const n = { ...d }; delete n[refreshPeriod]; return n; });
        setPeriodDetailLoading(s => new Set([...s, refreshPeriod]));
        api.get('outgoing-invoices/period-detail', { period: refreshPeriod })
          .then(data => setPeriodDetail(d => ({ ...d, [refreshPeriod]: data })))
          .catch(() => setPeriodDetail(d => ({ ...d, [refreshPeriod]: [] })))
          .finally(() => setPeriodDetailLoading(s => { const n = new Set(s); n.delete(refreshPeriod); return n; }));
      }
    } catch (e) {
      alert(e.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const periods      = analytics?.periods || [];
  const topPayees    = analytics?.top_payees || [];
  const latest       = periods[0];
  const previous     = periods[1];
  const latestTotal      = latest?.total_spend || 0;
  const latestSow        = latest?.sow_spend || 0;
  const latestOverhead   = latest?.overhead_spend || 0;
  const latestContractor = latest?.contractor_spend || 0;
  const totalRolling = periods
    .slice(0, granularity === 'monthly' ? 12 : granularity === 'quarterly' ? 4 : 1)
    .reduce((s, p) => s + (p.total_spend || 0), 0);
  const scheduleTotal = schedule.reduce((s, g) => s + (g.total || 0), 0);

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-extrabold text-navy">Outgoing Payments</h2>
          <div className="text-[9px] text-gray-500">All outgoing invoices — contractor labour, supplier, PO-linked</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {[['monthly','Monthly'],['quarterly','Quarterly'],['yearly','Yearly']].map(([id, label]) => (
              <button key={id} onClick={() => setGranularity(id)}
                className={`px-3 py-1.5 text-[10px] font-semibold rounded border transition-colors ${granularity === id ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                {label}
              </button>
            ))}
          </div>
          <button onClick={openModal}
            className="px-3 py-1.5 text-[10px] font-semibold bg-navy text-white rounded border border-navy hover:bg-navy/90 transition-colors">
            + Log Invoice
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: granularity === 'monthly' ? 'This Month' : granularity === 'quarterly' ? 'This Quarter' : 'This Year', value: latestTotal, color: 'text-navy', sub: latest && previous ? `prev ${fmt(previous.total_spend)}` : null, trend: trend(latestTotal, previous?.total_spend) },
          { label: 'SOW-Linked', value: latestSow, color: 'text-emerald-700', sub: latestTotal > 0 ? `${((latestSow/latestTotal)*100).toFixed(0)}% of spend` : null },
          { label: 'Overhead', value: latestOverhead, color: 'text-amber-600', sub: latestTotal > 0 ? `${((latestOverhead/latestTotal)*100).toFixed(0)}% of spend` : null },
          { label: 'Contractor Labour', value: latestContractor, color: 'text-purple-700', sub: latestTotal > 0 ? `${((latestContractor/latestTotal)*100).toFixed(0)}% of spend` : null },
        ].map(c => (
          <div key={c.label} className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="text-[8px] text-gray-400 uppercase tracking-wide mb-1">{c.label}</div>
            <div className={`text-xl font-extrabold ${c.color}`}>{fmt(c.value)}</div>
            {c.sub && <div className="text-[8px] text-gray-400 mt-0.5">{c.sub}{c.trend != null && <TrendBadge pct={c.trend} />}</div>}
          </div>
        ))}
      </div>

      {/* Payment Schedule */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
        <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-navy">Payment Schedule</span>
            <span className="text-[8px] text-gray-400 ml-2">Grouped by scheduled payment date</span>
          </div>
          <div className="flex items-center gap-2">
            <select value={scheduleStatus} onChange={e => setScheduleStatus(e.target.value)}
              className="text-[9px] border border-gray-200 rounded px-2 py-1 bg-white">
              <option value="all">All statuses</option>
              <option value="submitted">Submitted</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
            </select>
            <select value={horizonDays} onChange={e => setHorizonDays(e.target.value)}
              className="text-[9px] border border-gray-200 rounded px-2 py-1 bg-white">
              <option value="30">Next 30 days</option>
              <option value="60">Next 60 days</option>
              <option value="90">Next 90 days</option>
              <option value="180">Next 6 months</option>
            </select>
            {scheduleTotal > 0 && <span className="text-[9px] font-bold text-navy">{fmtFull(scheduleTotal)} total</span>}
          </div>
        </div>
        {scheduleLoading ? (
          <div className="p-6 text-center text-[10px] text-gray-400">Loading…</div>
        ) : schedule.length === 0 ? (
          <div className="p-6 text-center text-[10px] text-gray-400">
            No invoices with a scheduled payment date in the selected window.
            <div className="mt-1 text-[9px] text-gray-300">Set a scheduled payment date on any invoice to see it here.</div>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {schedule.map((group) => (
              <div key={group.date} className="px-3 py-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-navy">{group.date}</span>
                    <span className="text-[8px] text-gray-400">{group.invoices.length} invoice{group.invoices.length !== 1 ? 's' : ''}</span>
                  </div>
                  <span className="text-[10px] font-extrabold text-navy">{fmtFull(group.total)}</span>
                </div>
                <div className="space-y-1">
                  {group.invoices.map((inv) => {
                    const canDelete = inv.domain === 'supplier' || (inv.domain === 'contractor' && inv.source === 'manual');
                    return (
                      <div key={inv.id} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <DomainBadge domain={inv.domain} />
                          {inv.source === 'manual' && <span className="text-[7px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">Manual</span>}
                          <span className="text-[9px] font-semibold text-gray-700 truncate max-w-[160px]">{inv.payee_name || '—'}</span>
                          {inv.invoice_number && <span className="text-[8px] text-gray-400 hidden sm:inline">#{inv.invoice_number}</span>}
                          {inv.sow_ref && <span className="text-[7px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{inv.sow_ref}</span>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[8px] text-gray-400">{inv.status}</span>
                          <span className="text-[9px] font-bold text-gray-800">{fmtFull(inv.amount)}</span>
                          {canDelete && (
                            <button onClick={() => setDeleteTarget({ id: inv.id, domain: inv.domain, label: inv.payee_name || inv.invoice_number || inv.id })}
                              className="text-[8px] text-red-400 hover:text-red-600 px-1">✕</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Period breakdown + Top payees */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="col-span-2 bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <span className="text-[10px] font-bold text-navy">Spend by Period</span>
            <span className="text-[8px] text-gray-400">{periods.length} periods · {fmt(totalRolling)} rolling</span>
          </div>
          {analyticsLoading ? (
            <div className="p-6 text-center text-[10px] text-gray-400">Loading…</div>
          ) : periods.length === 0 ? (
            <div className="p-6 text-center text-[10px] text-gray-400">No invoice data available</div>
          ) : (
            <div className="overflow-x-auto"><table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="px-3 py-2 text-left text-[8px] font-bold text-gray-400 uppercase">Period</th>
                  <th className="px-2 py-2 text-right text-[8px] font-bold text-gray-400 uppercase">Inv</th>
                  <th className="px-3 py-2 text-right text-[8px] font-bold text-gray-400 uppercase">SOW-linked</th>
                  <th className="px-3 py-2 text-right text-[8px] font-bold text-gray-400 uppercase">Overhead</th>
                  <th className="px-3 py-2 text-right text-[8px] font-bold text-gray-400 uppercase">Labour</th>
                  <th className="px-3 py-2 text-right text-[8px] font-bold text-gray-400 uppercase">Total</th>
                  <th className="px-2 py-2 text-right text-[8px] font-bold text-gray-400 uppercase">vs prev</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((p, i) => {
                  const prev = periods[i + 1];
                  const chg = trend(p.total_spend, prev?.total_spend);
                  const maxSpend = Math.max(...periods.map(x => x.total_spend || 0));
                  const barW = maxSpend > 0 ? ((p.total_spend || 0) / maxSpend) * 100 : 0;
                  const isExpanded = expandedPeriod === p.period;
                  const detail = periodDetail[p.period];
                  const detailLoading = periodDetailLoading.has(p.period);
                  return (
                    <Fragment key={p.period}>
                      <tr onClick={() => togglePeriod(p.period)}
                        className={`border-b border-gray-50 cursor-pointer select-none ${i === 0 ? 'bg-navy/3' : ''} ${isExpanded ? 'bg-blue-50/20' : 'hover:bg-gray-50/50'}`}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <span className="text-[8px] text-gray-400 w-2">{isExpanded ? '▾' : '▸'}</span>
                            <div>
                              <div className="text-[10px] font-semibold text-gray-800">{p.period}</div>
                              <div className="h-1 bg-gray-100 rounded mt-1 w-20">
                                <div className="h-1 bg-navy/60 rounded" style={{ width: `${barW}%` }} />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-right text-[9px] text-gray-500">{p.invoice_count}</td>
                        <td className="px-3 py-2 text-right"><div className="text-[9px] font-semibold text-emerald-700">{fmtFull(p.sow_spend)}</div></td>
                        <td className="px-3 py-2 text-right"><div className="text-[9px] font-semibold text-amber-600">{fmtFull(p.overhead_spend)}</div></td>
                        <td className="px-3 py-2 text-right">
                          <div className="text-[9px] font-semibold text-purple-700">{fmtFull(p.contractor_spend)}</div>
                          {p.contractor_count > 0 && <div className="text-[7px] text-gray-400">{p.contractor_count} inv</div>}
                        </td>
                        <td className="px-3 py-2 text-right text-[10px] font-bold text-navy">{fmtFull(p.total_spend)}</td>
                        <td className="px-2 py-2 text-right">
                          <TrendBadge pct={chg} />
                          {chg == null && <span className="text-[8px] text-gray-300">—</span>}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-gray-100 bg-gray-50/40">
                          <td colSpan={7} className="px-4 py-2">
                            {detailLoading ? (
                              <div className="text-[9px] text-gray-400 py-1">Loading…</div>
                            ) : !detail || detail.length === 0 ? (
                              <div className="text-[9px] text-gray-400 py-1">No invoices found for this period.</div>
                            ) : (
                              <div className="space-y-1 py-1">
                                {detail.map(inv => {
                                  const canDelete = inv.domain === 'supplier' || (inv.domain === 'contractor' && inv.source === 'manual');
                                  return (
                                    <div key={inv.id} className="flex items-center justify-between bg-white rounded px-2 py-1 border border-gray-100">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <DomainBadge domain={inv.domain} />
                                        {inv.source === 'manual' && <span className="text-[7px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">Manual</span>}
                                        <span className="text-[9px] font-semibold text-gray-700 truncate max-w-[180px]">{inv.payee_name || '—'}</span>
                                        {inv.invoice_number && <span className="text-[8px] text-gray-400">#{inv.invoice_number}</span>}
                                        {inv.sow_ref && <span className="text-[7px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{inv.sow_ref}</span>}
                                        {inv.inv_date && <span className="text-[8px] text-gray-300">{inv.inv_date}</span>}
                                      </div>
                                      <div className="flex items-center gap-2 shrink-0">
                                        <span className="text-[8px] text-gray-400">{inv.status}</span>
                                        <span className="text-[9px] font-bold text-gray-800">{fmtFull(inv.amount)}</span>
                                        {canDelete && (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: inv.id, domain: inv.domain, label: inv.payee_name || inv.invoice_number || inv.id, refreshPeriod: p.period }); }}
                                            className="text-[8px] text-red-400 hover:text-red-600 px-1">✕</button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>

        {/* Top payees */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
            <span className="text-[10px] font-bold text-navy">Top Payees (12m)</span>
          </div>
          {topPayees.length === 0 ? (
            <div className="p-4 text-center text-[10px] text-gray-400">No data</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {topPayees.map((p, i) => {
                const maxS = topPayees[0]?.total_spend || 1;
                const barColor = p.domain === 'contractor' ? 'bg-purple-400' : 'bg-amber-400';
                return (
                  <div key={p.payee_name + i} className="px-3 py-2">
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[8px] text-gray-300 w-4">{i + 1}</span>
                        <DomainBadge domain={p.domain} />
                        <span className="text-[9px] font-semibold text-gray-700 truncate max-w-[90px]">{p.payee_name}</span>
                      </div>
                      <span className="text-[9px] font-bold text-navy">{fmt(p.total_spend)}</span>
                    </div>
                    <div className="h-1 bg-gray-100 rounded ml-6">
                      <div className={`h-1 ${barColor} rounded`} style={{ width: `${(p.total_spend / maxS) * 100}%` }} />
                    </div>
                    <div className="text-[7px] text-gray-400 ml-6 mt-0.5">{p.invoice_count} invoice{p.invoice_count !== 1 ? 's' : ''}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Manual invoice modal */}
      {showModal && (
        <Modal title="Log Invoice Manually" onClose={() => setShowModal(false)}>
            <div>
              <div className="text-[9px] text-gray-400 mb-3">For invoices received outside the accounts inbox</div>
              <div className="flex gap-2 mb-4">
                {[['supplier','Supplier Invoice'],['contractor','Contractor / Labour']].map(([t, label]) => (
                  <button key={t} onClick={() => setInvoiceType(t)}
                    className={`flex-1 py-2 text-[10px] font-semibold rounded border transition-colors ${invoiceType === t ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
              {invoiceType === 'supplier' ? (
                <SupplierForm form={supplierForm} setForm={setSupplierForm} sows={sows} pos={pos} suppliers={suppliers} categories={categories} />
              ) : (
                <ContractorForm form={contractorForm} setForm={setContractorForm} sows={sows} pos={pos} />
              )}
              {saveError && <div className="mt-3 text-[10px] text-red-600 bg-red-50 rounded px-3 py-2">{saveError}</div>}
              <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100 sticky bottom-0 -mx-4 -mb-3 px-4 pb-3 bg-white">
                <button onClick={() => setShowModal(false)}
                  className="flex-1 py-2 text-[10px] font-semibold rounded border border-gray-200 text-gray-600 hover:bg-gray-50">
                  Cancel
                </button>
                <button onClick={saveManualInvoice} disabled={saving}
                  className="flex-1 py-2 text-[10px] font-semibold rounded bg-navy text-white hover:bg-navy/90 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Log Invoice'}
                </button>
              </div>
            </div>
        </Modal>
      )}

      {/* Delete confirm dialog */}
      {deleteTarget && (
        <Modal title={<span className="text-red-600">Delete Invoice?</span>} onClose={() => { if (!deleting) setDeleteTarget(null); }}>
            <div className="text-[10px] text-gray-600 mb-4">
              This will permanently remove the invoice for <strong>{deleteTarget.label}</strong>. This cannot be undone.
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDeleteTarget(null)}
                className="flex-1 py-2 text-[10px] font-semibold rounded border border-gray-200 text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={confirmDelete} disabled={deleting}
                className="flex-1 py-2 text-[10px] font-semibold rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, children, half }) {
  return (
    <div className={half ? 'flex-1 min-w-0' : 'w-full'}>
      <label className="block text-[9px] font-semibold text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inp = 'w-full border border-gray-200 rounded px-2 py-1.5 text-[10px] focus:outline-none focus:ring-1 focus:ring-navy/30';
const sel = 'w-full border border-gray-200 rounded px-2 py-1.5 text-[10px] bg-white focus:outline-none focus:ring-1 focus:ring-navy/30';

function SupplierForm({ form, setForm, sows, pos, suppliers, categories }) {
  const s = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const [newSupplier, setNewSupplier] = useState(false);
  return (
    <div className="space-y-3">
      <Field label="Supplier *">
        {!newSupplier ? (
          <div className="flex gap-1">
            <select className={sel + ' flex-1'} value={form.supplier_id} onChange={e => {
              const sup = suppliers.find(x => x.id === e.target.value);
              setForm(f => ({ ...f, supplier_id: e.target.value, supplier_name: sup?.name || f.supplier_name }));
            }}>
              <option value="">— Select supplier —</option>
              {suppliers.filter(s => s.is_active !== 0).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button type="button" onClick={() => setNewSupplier(true)}
              className="text-[9px] px-2 py-1.5 border border-gray-200 rounded text-navy hover:bg-gray-50 whitespace-nowrap">+ New</button>
          </div>
        ) : (
          <div className="flex gap-1">
            <input className={inp + ' flex-1'} value={form.supplier_name} onChange={s('supplier_name')} placeholder="Enter supplier name" autoFocus />
            <button type="button" onClick={() => setNewSupplier(false)}
              className="text-[9px] px-2 py-1.5 border border-gray-200 rounded text-gray-500 hover:bg-gray-50">List</button>
          </div>
        )}
      </Field>
      <div className="flex gap-2">
        <Field label="Invoice Number" half>
          <input className={inp} value={form.invoice_number} onChange={s('invoice_number')} placeholder="INV-001" />
        </Field>
        <Field label="Invoice Date" half>
          <input type="date" className={inp} value={form.invoice_date} onChange={s('invoice_date')} />
        </Field>
      </div>
      <div className="flex gap-2">
        <Field label="Net (excl. VAT)" half>
          <input type="number" step="0.01" className={inp} value={form.total_excl_vat} onChange={s('total_excl_vat')} placeholder="0.00" />
        </Field>
        <Field label="VAT" half>
          <input type="number" step="0.01" className={inp} value={form.vat_amount} onChange={s('vat_amount')} placeholder="0.00" />
        </Field>
      </div>
      <div className="flex gap-2">
        <Field label="Total (incl. VAT)" half>
          <input type="number" step="0.01" className={inp} value={form.total_incl_vat} onChange={s('total_incl_vat')} placeholder="0.00" />
        </Field>
        <Field label="Currency" half>
          <select className={sel} value={form.currency} onChange={s('currency')}>
            <option>GBP</option><option>USD</option><option>EUR</option>
          </select>
        </Field>
      </div>
      <div className="flex gap-2">
        <Field label="Link to SOW" half>
          <SowSelect sows={sows} value={form.sow_id} onChange={id => setForm(f => ({ ...f, sow_id: id }))} placeholder="— None —" inputClassName={sel} />
        </Field>
        <Field label="Link to PO" half>
          <PoSelect pos={pos} value={form.po_id} onChange={id => setForm(f => ({ ...f, po_id: id }))} placeholder="— None —" inputClassName={sel} />
        </Field>
      </div>
      <Field label="Overhead Category" >
        <select className={sel} value={form.overhead_category_id} onChange={s('overhead_category_id')} disabled={!!form.sow_id || !!form.po_id}>
          <option value="">— None —</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Scheduled Payment Date">
        <input type="date" className={inp} value={form.scheduled_payment_date} onChange={s('scheduled_payment_date')} />
      </Field>
      <Field label="Notes">
        <textarea className={inp + ' resize-none'} rows={2} value={form.notes} onChange={s('notes')} placeholder="Optional notes…" />
      </Field>
    </div>
  );
}

function ContractorForm({ form, setForm, sows, pos }) {
  const s = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <div className="space-y-3">
      <Field label="Payee / Contractor Name">
        <input className={inp} value={form.payee_name} onChange={s('payee_name')} placeholder="e.g. Jane Smith" />
      </Field>
      <div className="flex gap-2">
        <Field label="Invoice Number" half>
          <input className={inp} value={form.invoice_number} onChange={s('invoice_number')} placeholder="INV-001" />
        </Field>
        <Field label="Invoice Date" half>
          <input type="date" className={inp} value={form.invoice_date} onChange={s('invoice_date')} />
        </Field>
      </div>
      <div className="flex gap-2">
        <Field label="Period From" half>
          <input type="date" className={inp} value={form.period_from} onChange={s('period_from')} />
        </Field>
        <Field label="Period To" half>
          <input type="date" className={inp} value={form.period_to} onChange={s('period_to')} />
        </Field>
      </div>
      <div className="flex gap-2">
        <Field label="Amount (excl. VAT)" half>
          <input type="number" step="0.01" className={inp} value={form.total_amount} onChange={s('total_amount')} placeholder="0.00" />
        </Field>
        <Field label="VAT" half>
          <input type="number" step="0.01" className={inp} value={form.vat_amount} onChange={s('vat_amount')} placeholder="0.00" />
        </Field>
      </div>
      <div className="flex gap-2">
        <Field label="Total (incl. VAT)" half>
          <input type="number" step="0.01" className={inp} value={form.total_incl_vat} onChange={s('total_incl_vat')} placeholder="0.00" />
        </Field>
        <Field label="Currency" half>
          <select className={sel} value={form.currency} onChange={s('currency')}>
            <option>GBP</option><option>USD</option><option>EUR</option>
          </select>
        </Field>
      </div>
      <div className="flex gap-2">
        <Field label="Link to SOW" half>
          <SowSelect sows={sows} value={form.sow_id} onChange={id => setForm(f => ({ ...f, sow_id: id }))} placeholder="— None (overhead) —" inputClassName={sel} />
        </Field>
        <Field label="Link to PO" half>
          <PoSelect pos={pos} value={form.po_id} onChange={id => setForm(f => ({ ...f, po_id: id }))} placeholder="— None —" inputClassName={sel} />
        </Field>
      </div>
      <Field label="Scheduled Payment Date">
        <input type="date" className={inp} value={form.scheduled_payment_date} onChange={s('scheduled_payment_date')} />
      </Field>
      <Field label="Notes">
        <textarea className={inp + ' resize-none'} rows={2} value={form.notes} onChange={s('notes')} placeholder="Optional notes…" />
      </Field>
    </div>
  );
}
